import PropTypes from "prop-types";
import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaArrowsRotate } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import useCaptcha from "hooks/useCaptcha";
import { notify } from "utils/notify";

import { useResendConfirmationMutation } from "components/services/users/userService";

import paths from "routes/paths";

const ResendConfirmationForm = ({ hasLabel }) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  // The portal's image CAPTCHA, fetched as every form fetches it (hooks/useCaptcha).
  const { captcha, refreshCaptcha: fetchCaptcha } = useCaptcha();
  const [captchaInput, setCaptchaInput] = useState("");

  const [resendConfirmation, { isLoading }] = useResendConfirmationMutation();

  const onSubmit = async (data) => {
    try {
      const response = await resendConfirmation({
        email: data.email,
        captcha_id: captcha.id,
        captcha_input: captchaInput,
      }).unwrap();
      // Show the backend's own message (e.g. "check your email"); a warning when the e-mail
      // could not be queued (#136).
      if (response?.meta?.email_queued === false) {
        toast.warning(response.message, { theme: "colored" });
      } else {
        toast.success(response.message, { theme: "colored" });
      }
      navigate(paths.cardLogin);
    } catch (error) {
      notify.apiError(error, "UI_RESEND_CONFIRMATION_FAILED");
      // The backend consumes the CAPTCHA on every attempt (right or wrong),
      // so a retry needs a fresh challenge.
      setCaptchaInput("");
      fetchCaptcha();
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Email address</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? "Email address" : ""}
          type="email"
          isInvalid={!!errors.email}
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address",
            },
          })}
        />
        <Form.Control.Feedback type="invalid">
          {errors.email?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Enter the characters shown</Form.Label>}
        <div className="d-flex align-items-center mb-2">
          {captcha.url && (
            <img src={captcha.url} alt="CAPTCHA" style={{ height: 40 }} />
          )}
          <Button
            variant="link"
            type="button"
            onClick={fetchCaptcha}
            aria-label="Refresh CAPTCHA"
          >
            <FaArrowsRotate />
          </Button>
        </div>
        <Form.Control
          placeholder="CAPTCHA"
          value={captchaInput}
          onChange={(e) => setCaptchaInput(e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Button
          type="submit"
          color="primary"
          className="w-100"
          disabled={isLoading || !captchaInput}
        >
          Resend confirmation email
        </Button>
      </Form.Group>

      <Link className="fs-10" to={paths.cardLogin}>
        Return to login
      </Link>
    </Form>
  );
};

ResendConfirmationForm.propTypes = {
  hasLabel: PropTypes.bool,
};

ResendConfirmationForm.defaultProps = {
  hasLabel: false,
};

export default ResendConfirmationForm;
